import { useEffect, useRef, useState } from 'react'
import { apiBulkCheck, BulkCheckItem, BulkCheckResult } from '../helpers/apiHelpers'
import { ServiceDep } from './useFinalDeps'
import { useStableAccessToken } from './useStableAccessToken'

export function useBulkCheck(params: {
  finalServiceDeps: ServiceDep[],
  applicationBranch: string,
  initialBulkCheckItems?: BulkCheckItem[]
}) {
  const { finalServiceDeps, applicationBranch, initialBulkCheckItems } = params
  const tokenGetter = useStableAccessToken()
  const [bulkResults, setBulkResults] = useState<BulkCheckResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasCalledRef = useRef(false)
  const manualCalledRef = useRef(false)

  // One-time manual refresh path
  useEffect(() => {
    if (!initialBulkCheckItems || initialBulkCheckItems.length === 0) return
    if (manualCalledRef.current) return
    manualCalledRef.current = true

    setLoading(true)
    setError(null)

    apiBulkCheck(initialBulkCheckItems, tokenGetter)
      .then(({ results, errors }) => {
        setBulkResults(results)
        if (errors?.length) setError(`Validation incomplete — ${errors.length} service(s) could not be checked: ${errors.join('; ')}`)
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBulkCheckItems]) // tokenGetter is stable, don't include in deps

  // Normal bulk-check based on finalServiceDeps
  useEffect(() => {
    if (initialBulkCheckItems && initialBulkCheckItems.length > 0) return

    if (finalServiceDeps.length === 0) {
      setBulkResults([])
      return
    }
    if (hasCalledRef.current) return
    hasCalledRef.current = true

    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const items: BulkCheckItem[] = finalServiceDeps.map((dep) => ({
          appName: dep.name,
          repoName: dep.repoName,
          branch: applicationBranch,
        }))
        const { results, errors } = await apiBulkCheck(items, tokenGetter)
        setBulkResults(results)
        if (errors?.length) setError(`Validation incomplete — ${errors.length} service(s) could not be checked: ${errors.join('; ')}`)
      } catch (err: any) {
        setError(err.message || 'Bulk check error')
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalServiceDeps, applicationBranch, initialBulkCheckItems]) // tokenGetter is stable, don't include in deps

  return { bulkResults, loading, error, setBulkResults }
}


